import { useCallback, useState } from "react"
import { useDispatch } from "react-redux"
import { CircularProgress, Stack } from "@mui/material"
import { useGetCatalogoQuery, useGetLojaAtualQuery, type GrupoCatalogo, type LojaAtualResponse } from "@/api"
import CategoriasLateral from "@/components/catalogo-kiosk/categorias-lateral"
import DialogPassos from "@/components/catalogo-kiosk/dialog-passos"
import DialogDetalheProduto from "@/components/catalogo-kiosk/dialog-detalhe-produto"
import { DialogBusca } from "@/components/catalogo-kiosk/dialog-busca"
import GradeProdutos, { type CategoriaSelecionada } from "@/components/catalogo-kiosk/grade-produtos"
import { RodapeCarrinho } from "@/components/catalogo-kiosk/rodape-carrinho"
import { useCardapioKiosk } from "@/components/catalogo-kiosk/use-cardapio-kiosk"
import { CardapioVazioKiosk } from "@/components/catalogo-kiosk/vazio"
import { SOBRE_EMBUTIDO, SobreCardapio } from "@/components/sobre-cardapio"
import { useArgCatalogo } from "@/lib/arg-catalogo"
import { canalDoIdentificador } from "@/lib/canal"
import ScreenHeader from "../../checkout/components/screen-header"
import { irVista } from "../navegacao"

export default function Catalogo({ podeTrocarConsumo }: { podeTrocarConsumo: boolean }): JSX.Element {
    const dispatch = useDispatch()
    const { data: loja } = useGetLojaAtualQuery()
    const canal = loja ? canalDoIdentificador(loja.identificador) : undefined
    const { data, isLoading } = useGetCatalogoQuery(useArgCatalogo(canal), { pollingInterval: 60_000 })

    if (!loja || isLoading || !data) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, py: 8 }}>
                <CircularProgress />
            </Stack>
        )
    }

    return (
        <CardapioTotem
            loja={loja}
            grupos={data.grupos}
            aoVoltar={podeTrocarConsumo ? () => irVista(dispatch, { currentView: 'VIEW_CONSUMO' }) : undefined}
            aoAbrirCarrinho={() => irVista(dispatch, { currentView: 'VIEW_CARRINHO' })}
            aoFinalizar={() => irVista(dispatch, { currentView: 'VIEW_FINALIZAR' })}
        />
    )
}

interface CardapioTotemProps {
    loja: LojaAtualResponse
    grupos: GrupoCatalogo[]
    aoVoltar?: () => void
    aoAbrirCarrinho: () => void
    aoFinalizar: () => void
}

export function CardapioTotem({ loja, grupos, aoVoltar, aoAbrirCarrinho, aoFinalizar }: CardapioTotemProps): JSX.Element {
    const cardapio = useCardapioKiosk(grupos)
    const { carrinho } = cardapio
    const [categoria, setCategoria] = useState<CategoriaSelecionada>(null)
    const [buscaAberta, setBuscaAberta] = useState(false)
    const abrirBusca = useCallback(() => setBuscaAberta(true), [])

    return (
        <Stack
            sx={{
                width: '100%',
                height: '100vh',
                '@supports (height: 100dvh)': { height: '100dvh' },
                overflow: 'hidden',
                boxSizing: 'border-box',
                userSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitTapHighlightColor: 'transparent',
                '& input, & textarea': { userSelect: 'text' },
            }}
        >
            <ScreenHeader
                title={loja.configuracao.nome}
                subTitle="Faça seu pedido"
                logoUrl={loja.configuracao.urlLogo}
                onClickBack={aoVoltar}
            />

            {grupos.length === 0 ? (
                <CardapioVazioKiosk />
            ) : (
                <Stack direction="row" sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <CategoriasLateral
                        grupos={grupos}
                        selectedCategoryId={categoria}
                        onCategorySelect={setCategoria}
                        rodape={SOBRE_EMBUTIDO}
                    />

                    <GradeProdutos
                        grupos={grupos}
                        selectedCategoryId={categoria}
                        renderCard={cardapio.renderCard}
                        aoBuscar={abrirBusca}
                    />
                </Stack>
            )}

            <RodapeCarrinho
                totalItens={carrinho.totalItens}
                subtotal={carrinho.subtotal}
                aoAbrirCarrinho={aoAbrirCarrinho}
                aoFinalizar={aoFinalizar}
            />

            {grupos.length === 0 && <SobreCardapio />}

            <DialogPassos
                produto={cardapio.produtoPersonalizar}
                permitirObservacao
                permitirAdicionalPorSabor
                onFechar={cardapio.fecharPersonalizacao}
                onConfirmar={cardapio.confirmarPersonalizado}
            />

            <DialogDetalheProduto
                produto={cardapio.produtoDetalhe}
                aparelho="totem"
                onFechar={cardapio.fecharDetalhes}
                onConfirmar={cardapio.confirmarDetalhe}
            />

            <DialogBusca
                aberto={buscaAberta}
                grupos={grupos}
                renderCard={cardapio.renderCard}
                aoFechar={() => setBuscaAberta(false)}
            />
        </Stack>
    )
}
